/**
 * 名場面の静止画（共有カードに載せる盤面の写真）。
 *
 * 動画の録画（HighlightRecorder）は端末の負荷があるので既定で切ってあるが、
 * 静止画は「その瞬間に1枚写すだけ」なので、いつも撮っておく（2026-10-09）。
 *
 * 1プレイで残すのは3枚だけ：
 * - 最高Lvの料理ができた瞬間（届いた最高レベルが上がるたびに撮り直す）
 * - 最大コンボの瞬間（コンボ3以上で、このプレイの最大を超えるたびに撮り直す）
 * - 最後の盤面（こぼれた料理が赤く光っているところ）
 *
 * ゲームの画面は WebGL なので、描き終わった直後（Phaser の postrender）でないと
 * 中身を読み出せない。撮りたいときは「次に描き終わったら写す」を予約しておく。
 *
 * 写すたびに GPU から画面を読み出す（古い端末では一瞬止まる）ので、回数を絞る。
 * 同じ種類の予約は、新しい記録が来るたびに待ち直す（連鎖が続いているあいだは写さず、
 * 落ち着いたところで1回だけ写す）。1プレイで数回に収まる。
 */

export type MomentKind = 'level' | 'combo' | 'final';

export interface Moment {
  kind: MomentKind;
  /** 結果画面の選択肢と、カードの盤面の下に書く言葉。 */
  label: string;
  /** レベル・コンボ数（最後の盤面は 0）。 */
  value: number;
  image: HTMLCanvasElement;
}

/** 名場面として撮りはじめるコンボ数（2 はよく起きるので 3 から）。 */
export const MOMENT_MIN_COMBO = 3;
/** 名場面として撮りはじめるレベル（序盤の小さい料理は撮らない）。 */
export const MOMENT_MIN_LEVEL = 4;
/** 合体のあと、ふくらむ演出が見えるまで待つ（その間に次の段ができたら待ち直す）。 */
const LEVEL_DELAY_MS = 320;
/** 連鎖が止まってから写すまで（「コンボN!」の字はまだ残っている長さ）。 */
const COMBO_DELAY_MS = 420;
/** こぼれた料理に赤い輪が出てから写す（輪が点滅で薄い瞬間を避ける）。 */
const FINAL_DELAY_MS = 380;

/** 選択肢に並べる順（いちばん自慢できるものを先に）。 */
const ORDER: MomentKind[] = ['level', 'combo', 'final'];

export function momentLabel(kind: MomentKind, value: number, name?: string): string {
  switch (kind) {
    case 'level':
      return name ? `Lv${value} ${name}ができた瞬間` : `Lv${value}ができた瞬間`;
    case 'combo':
      return `コンボ${value}の瞬間`;
    default:
      return '最後の盤面';
  }
}

/** 選択肢の短い呼び名（結果画面の小さいボタン）。 */
export function momentShortLabel(moment: Pick<Moment, 'kind' | 'value'>): string {
  switch (moment.kind) {
    case 'level':
      return `Lv${moment.value}`;
    case 'combo':
      return `コンボ${moment.value}`;
    default:
      return '最後';
  }
}

/** 最初に選んでおく1枚（最高Lv → 最大コンボ → 最後の盤面）。 */
export function defaultMoment(moments: readonly Moment[]): Moment | null {
  for (const kind of ORDER) {
    const found = moments.find((m) => m.kind === kind);
    if (found) return found;
  }
  return null;
}

interface Request {
  kind: MomentKind;
  value: number;
  label: string;
}

export class MomentCapture {
  private readonly moments = new Map<MomentKind, Moment>();
  private pending: Request[] = [];
  /** 種類ごとの待ち（新しい予約が来たら前の待ちは捨てる）。 */
  private readonly timers = new Map<MomentKind, number>();
  /** 撮り直しの判定用（予約した時点の値。写し終わる前に次が来ても取りこぼさない）。 */
  private bestLevel = 0;
  private bestCombo = 0;
  private unsubscribe: (() => void) | null = null;

  constructor(
    private readonly getCanvas: () => HTMLCanvasElement | null | undefined,
    onFrame: (draw: () => void) => () => void,
  ) {
    this.unsubscribe = onFrame(() => this.flush());
  }

  /** 新しいプレイの始まり（コンティニューでは呼ばない）。 */
  reset(): void {
    this.moments.clear();
    this.pending = [];
    this.timers.forEach((timer) => window.clearTimeout(timer));
    this.timers.clear();
    this.bestLevel = 0;
    this.bestCombo = 0;
  }

  /** このプレイで届いた最高レベルが上がった。 */
  noteLevel(level: number, name?: string): void {
    if (level < MOMENT_MIN_LEVEL || level <= this.bestLevel) return;
    this.bestLevel = level;
    this.schedule({ kind: 'level', value: level, label: momentLabel('level', level, name) }, LEVEL_DELAY_MS);
  }

  /** コンボ数が変わった。 */
  noteCombo(combo: number): void {
    if (combo < MOMENT_MIN_COMBO || combo <= this.bestCombo) return;
    this.bestCombo = combo;
    this.schedule({ kind: 'combo', value: combo, label: momentLabel('combo', combo) }, COMBO_DELAY_MS);
  }

  /** こぼれて終わりはじめた（赤い輪が出ている）。 */
  noteEnding(): void {
    this.schedule({ kind: 'final', value: 0, label: momentLabel('final', 0) }, FINAL_DELAY_MS);
  }

  /** このプレイの名場面（自慢できる順）。 */
  list(): Moment[] {
    return ORDER.map((kind) => this.moments.get(kind)).filter((m): m is Moment => Boolean(m));
  }

  dispose(): void {
    this.reset();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private schedule(request: Request, delayMs: number): void {
    const previous = this.timers.get(request.kind);
    if (previous !== undefined) window.clearTimeout(previous);
    const timer = window.setTimeout(() => {
      this.timers.delete(request.kind);
      // 同じ種類の予約は新しいほうだけ残す
      this.pending = [...this.pending.filter((p) => p.kind !== request.kind), request];
    }, delayMs);
    this.timers.set(request.kind, timer);
  }

  /** ゲームが1枚描き終わった。予約があれば写す。 */
  private flush(): void {
    if (this.pending.length === 0) return;
    const source = this.getCanvas();
    if (!source || source.width === 0 || source.height === 0) return;
    const requests = this.pending;
    this.pending = [];
    let image: HTMLCanvasElement;
    try {
      image = copyCanvas(source);
    } catch {
      return;
    }
    for (const request of requests) {
      const previous = this.moments.get(request.kind);
      // 同じ種類なら、大きいほう（最後の盤面はいつも新しいほう）
      if (previous && request.kind !== 'final' && previous.value > request.value) continue;
      this.moments.set(request.kind, { ...request, image });
    }
  }
}

/** 盤面を写す。論理解像度が 480x720 なので、そのままの大きさで足りる。 */
function copyCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const maxWidth = 720;
  const scale = Math.min(1, maxWidth / source.width);
  const out = document.createElement('canvas');
  out.width = Math.round(source.width * scale);
  out.height = Math.round(source.height * scale);
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(source, 0, 0, out.width, out.height);
  return out;
}

/** 結果画面の選択肢に出す小さな絵。 */
export function momentThumbnail(moment: Moment, width = 96): HTMLCanvasElement {
  const src = moment.image;
  const out = document.createElement('canvas');
  const ratio = src.height / Math.max(1, src.width);
  // 高精細の画面でもにじまないよう、2倍で描いて CSS で縮める
  out.width = width * 2;
  out.height = Math.round(width * ratio * 2);
  out.getContext('2d')?.drawImage(src, 0, 0, out.width, out.height);
  return out;
}
